import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';
import { AlertCircle, Building2, CheckCircle, Crown, Gift, Loader2, Star, Tag, Zap } from 'lucide-react';
import {
  getAllSubscriptionPlans,
  getCurrentSubscription,
  SubscriptionPlan,
} from '../../../services/subscription.service';

/* Previous mock pricing data retained for reference.
const PRIVATE_PLANS = [
  { id: 'free', name: 'Free', price: { monthly: 0, annual: 0 }, listings: 1,
    features: ['1 active listing', 'Basic search visibility', 'WhatsApp contact', 'Standard support'],
    notIncluded: ['Featured listing', 'Analytics', 'Boost listing', 'Priority support'] },
  { id: 'basic', name: 'Basic', price: { monthly: 99, annual: 79 }, listings: 3,
    features: ['3 active listings', 'Standard visibility', 'Analytics dashboard', 'Email support', 'WhatsApp contact'],
    notIncluded: ['Featured listing', 'Homepage visibility', 'Priority support'] },
  { id: 'pro', name: 'Pro', price: { monthly: 249, annual: 199 }, listings: 10,
    features: ['10 active listings', '1 featured listing', 'Homepage exposure', 'Advanced analytics', 'Priority support', 'Boost listing 2x'],
    notIncluded: ['Unlimited listings'] },
  { id: 'premium', name: 'Premium', price: { monthly: 499, annual: 399 }, listings: -1,
    features: ['Unlimited listings', '5 featured listings', 'Homepage priority', 'Full analytics', 'Dedicated support', 'CRM access', 'Custom branding'],
    notIncluded: [] },
];

const DEALER_PLANS = [
  { id: 'starter', name: 'Starter', price: { monthly: 499, annual: 399 }, listings: 20,
    features: ['20 listings', 'Verified dealer badge', 'Basic CRM', 'Standard support', 'Analytics'] },
  { id: 'business', name: 'Business', price: { monthly: 999, annual: 799 }, listings: 52,
    features: ['52 listings', 'Featured listings (5)', 'Full CRM', 'Priority support', 'Advanced analytics', 'Team accounts (3)', 'Lead assignment'] },
  { id: 'enterprise', name: 'Enterprise', price: { monthly: 1999, annual: 1599 }, listings: -1,
    features: ['Unlimited listings', 'Featured listings (20)', 'Enterprise CRM', 'Dedicated support', 'Multi-branch', 'Unlimited team accounts', 'API access', 'Custom reports'] },
];

const comparisonFeatures = [
  { feature: 'Active Listings', vals: ['1', '3', '10', 'Unlimited'] },
  { feature: 'Featured Listings', vals: ['No', 'No', '1', '5'] },
  { feature: 'Analytics', vals: ['No', 'Yes', 'Yes', 'Yes'] },
  { feature: 'Priority Support', vals: ['No', 'No', 'Yes', 'Yes'] },
  { feature: 'CRM Access', vals: ['No', 'No', 'No', 'Yes'] },
  { feature: 'Homepage Exposure', vals: ['No', 'No', 'Yes', 'Yes'] },
];
*/

type PricingType = 'private' | 'dealer';
type BillingPeriod = 'daily' | 'weekly' | 'monthly' | 'annual';

const BILLING_PERIODS: Array<{ value: BillingPeriod; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
];

const getBillingPeriod = (plan: SubscriptionPlan): BillingPeriod | null => {
  const validityType = String(plan.ValidityType || '').toLowerCase();
  if (validityType.includes('day')) return 'daily';
  if (validityType.includes('week')) return 'weekly';
  if (validityType.includes('month')) return 'monthly';
  if (validityType.includes('year') || validityType.includes('annual')) return 'annual';
  return null;
};

const localizedText = (
  value: unknown,
  fallback: string,
  language: string,
): string => {
  if (!value || typeof value !== 'object') return fallback;
  const translations = value as Record<string, unknown>;
  const languageCode = language.split('-')[0];
  const translated = translations[languageCode] ?? translations.en;
  return typeof translated === 'string' && translated.trim() ? translated : fallback;
};

const validityLabel = (plan: SubscriptionPlan): string => {
  const type = String(plan.ValidityType || '').trim();
  const value = Number(plan.ValidityValue || 0);
  if (!type || !value) return '';
  const normalizedType = value === 1 ? type.replace(/s$/i, '') : type;
  return `${value} ${normalizedType}`;
};

// TODO: Replace this name-based mapping with an Admin-managed IconKey from the API.
const getPlanIcon = (name: string, isFree: boolean) => {
  const normalizedName = name.toLowerCase();
  if (isFree) return Gift;
  if (normalizedName.includes('basic') || normalizedName.includes('starter')) return Zap;
  if (normalizedName.includes('pro') || normalizedName.includes('business')) return Star;
  if (normalizedName.includes('premium')) return Crown;
  if (normalizedName.includes('enterprise')) return Building2;
  return Tag;
};

export function Pricing() {
  const { t, i18n } = useTranslation('auth');
  const token = Cookies.get('token');
  const loggedInCustomerType = Number(Cookies.get('customerType'));
  const hasSellerAccount = Boolean(token) && [2, 3].includes(loggedInCustomerType);
  const [type, setType] = useState<PricingType>(loggedInCustomerType === 3 ? 'dealer' : 'private');
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentPlanName, setCurrentPlanName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true);
        setError('');
        const subscriptionCustomerType = type === 'private' ? 1 : 2;
        const planData = await getAllSubscriptionPlans(subscriptionCustomerType);
        setPlans(planData);
        const availablePeriods = BILLING_PERIODS.filter(period =>
          planData.some(plan => getBillingPeriod(plan) === period.value),
        );
        setBillingPeriod(currentPeriod =>
          availablePeriods.some(period => period.value === currentPeriod)
            ? currentPeriod
            : availablePeriods[0]?.value || 'monthly',
        );

        const isViewingOwnType =
          (loggedInCustomerType === 2 && type === 'private') ||
          (loggedInCustomerType === 3 && type === 'dealer');

        if (token && isViewingOwnType) {
          const current = await getCurrentSubscription();
          setCurrentPlanName(current?.planName || '');
        } else {
          setCurrentPlanName('');
        }
      } catch (requestError: any) {
        setPlans([]);
        setCurrentPlanName('');
        setError(requestError?.response?.data?.message || 'Unable to load pricing plans.');
      } finally {
        setLoading(false);
      }
    };

    void loadPlans();
  }, [type, token, loggedInCustomerType]);

  const availableBillingPeriods = BILLING_PERIODS.filter(period =>
    plans.some(plan => getBillingPeriod(plan) === period.value),
  );
  const filteredPlans = plans.filter(plan => getBillingPeriod(plan) === billingPeriod);

  const destination = token && loggedInCustomerType === 2
    ? '/dashboard/subscription'
    : token && loggedInCustomerType === 3
      ? '/dealer/subscription'
      : type === 'dealer'
        ? '/sell/professional'
        : '/sell';

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <div className="bg-gradient-to-br from-[#2D323A] to-[#1E2229] text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl text-white mb-3">{t('pricing.title')}</h1>
          <p className="text-white/70 mb-8">{t('pricing.subtitle')}</p>

          <div className="flex justify-center">
            <div className="bg-white/10 p-1 rounded-xl flex">
              {(!hasSellerAccount || loggedInCustomerType === 2) && (
                <button
                  type="button"
                  onClick={() => setType('private')}
                  className={`px-6 py-2 rounded-lg transition-colors text-sm ${
                    type === 'private' ? 'bg-white text-[#2D323A]' : 'text-white/70 hover:text-white'
                  }`}
                >
                  {t('pricing.tabs.private')}
                </button>
              )}
              {(!hasSellerAccount || loggedInCustomerType === 3) && (
                <button
                  type="button"
                  onClick={() => setType('dealer')}
                  className={`px-6 py-2 rounded-lg transition-colors text-sm ${
                    type === 'dealer' ? 'bg-white text-[#2D323A]' : 'text-white/70 hover:text-white'
                  }`}
                >
                  {t('pricing.tabs.dealer')}
                </button>
              )}
            </div>
          </div>

          <div className="flex justify-center mt-4">
            <div className="bg-white/10 p-1 rounded-xl flex">
              {availableBillingPeriods.map(period => (
                <button
                  key={period.value}
                  type="button"
                  onClick={() => setBillingPeriod(period.value)}
                  className={`px-5 py-2 rounded-lg transition-colors text-sm ${
                    billingPeriod === period.value
                      ? 'bg-[#1E6DFF] text-white'
                      : 'text-white/70 hover:text-white'
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-10">
        {loading && (
          <div className="flex items-center justify-center gap-3 py-16 text-[#717182]">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading plans...</span>
          </div>
        )}

        {!loading && error && (
          <div className="max-w-xl mx-auto flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && filteredPlans.length === 0 && (
          <div className="text-center py-16 text-[#717182]">
            No active {billingPeriod} plans are currently available.
          </div>
        )}

        {!loading && !error && filteredPlans.length > 0 && (
          <div className={`grid gap-5 ${
            filteredPlans.length >= 4
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
              : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          }`}>
            {filteredPlans.map(plan => {
              const name = localizedText(
                plan.SubscriptionNameML,
                plan.SubscriptionName,
                i18n.language,
              );
              const description = localizedText(
                plan.DescriptionML,
                plan.Description || '',
                i18n.language,
              );
              const isCurrent = Boolean(currentPlanName) &&
                currentPlanName.toLowerCase() === plan.SubscriptionName.toLowerCase();
              const isFree = Number(plan.Price) === 0;
              const validity = validityLabel(plan);
              const PlanIcon = getPlanIcon(name, isFree);

              return (
                <div
                  key={plan.id}
                  className={`bg-white rounded-2xl border-2 p-6 relative ${
                    isCurrent ? 'border-[#1E6DFF] shadow-xl' : 'border-[#E5E7EB]'
                  }`}
                >
                  {isCurrent && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#1E6DFF] text-white text-xs rounded-full whitespace-nowrap">
                      Current Plan
                    </div>
                  )}
                  <div className="h-8 mb-3 flex items-center gap-2">
                    <PlanIcon className="w-7 h-7 text-[#1E6DFF]" />
                    {isFree && (
                      <span className="px-2 py-1 rounded-md bg-[#1E6DFF] text-white text-xs font-medium">FREE</span>
                    )}
                    {!isFree && plan.IsTrial && (
                      <span className="px-2 py-1 rounded-md bg-[#FFF7E6] text-[#D97706] text-xs font-medium">TRIAL</span>
                    )}
                  </div>
                  <h2 className="text-xl text-[#2D323A] mb-1">{name}</h2>
                  <div className="mb-3">
                    <span className="text-3xl text-[#2D323A]">
                      {isFree ? t('pricing.billing.free') : Number(plan.Price).toLocaleString()}
                    </span>
                    {!isFree && <span className="text-[#717182]"> MAD</span>}
                  </div>
                  {description && <p className="text-sm text-[#717182] min-h-10 mb-4">{description}</p>}

                  <div className="space-y-2 mb-5">
                    <div className="flex items-center gap-2 text-sm text-[#2D323A]">
                      <CheckCircle className="w-4 h-4 text-[#22C55E] flex-shrink-0" />
                      {plan.NumberOfListings < 0
                        ? t('pricing.listing.unlimited')
                        : t('pricing.listing.upTo', { count: plan.NumberOfListings })}
                    </div>
                    {validity && (
                      <div className="flex items-center gap-2 text-sm text-[#2D323A]">
                        <CheckCircle className="w-4 h-4 text-[#22C55E] flex-shrink-0" />
                        Valid for {validity}
                      </div>
                    )}
                  </div>

                  {isCurrent ? (
                    <div className="w-full py-2.5 rounded-xl text-center text-sm bg-[#EAF2FF] text-[#1E6DFF]">
                      Current Plan
                    </div>
                  ) : (
                    <Link
                      to={destination}
                      className="block w-full py-2.5 rounded-xl text-center text-sm bg-[#1E6DFF] text-white hover:bg-[#1557d9] transition-colors"
                    >
                      {isFree ? t('pricing.buttons.getStartedFree') : t('pricing.buttons.getStarted')}
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center mt-12">
          <p className="text-[#717182] mb-2">{t('pricing.notSure')}</p>
          <Link to="/contact" className="text-[#1E6DFF] hover:underline">
            {t('pricing.buttons.contactGuidance')}
          </Link>
        </div>
      </div>
    </div>
  );
}
